// apps/web/src/routes/$lang/_layout/stays.$city.index.tsx
import { createFileRoute, redirect } from "@tanstack/react-router";
import { isStayPropertyType } from "@majourneys/shared";

export interface StaysCitySearch {
  checkin?: string;
  checkout?: string;
  adults?: number;
  /**
   * Comma separated child ages, one per child. Declared even though this route
   * only forwards it: an UNdeclared param reaches the next page in whatever
   * shape the router guesses, and "childAges=5" arrived as the number 5, which
   * crashed the page on .split(). Declaring it pins the type.
   */
  childAges?: string;
  /** Slug of the city the list is narrowed to. */
  city?: string;
  /** Which sort the guest picked. Absent means the default, cheapest first. */
  sort?: string;
}

/**
 * Nothing renders here any more — every URL under it redirects.
 *
 * Hotels are listed at /category/{type}, alongside Camp and Villa, because to
 * a guest they are one catalogue of places to stay and clicking "Hotels" in
 * the subcategory row should not throw them onto a different URL family. What
 * genuinely differs is how they are paid: hotels are charged online in full
 * at booking, camps are paid online from a link our team emails, and the listing page states
 * that above its first card, which is where a guest will actually read it.
 *
 * The two shapes this catches:
 *   /stays/riad      → /category/riad     (the type list, moved)
 *   /stays/marrakech → /category/stays     (old per-city hub, retired earlier)
 *
 * Both 301, not the default 307: these moves are permanent and we published
 * the old URLs ourselves. Property pages beneath this
 * (/stays/marrakech/la-mamounia-marrakech) are a different route file and are
 * untouched — the param keeps the name `city` for that reason.
 */
export const Route = createFileRoute("/$lang/_layout/stays/$city/")({
  validateSearch: (search: Record<string, unknown>): StaysCitySearch => {
    const checkin = typeof search.checkin === "string" ? search.checkin : undefined;
    const checkout = typeof search.checkout === "string" ? search.checkout : undefined;
    const adultsRaw = search.adults;
    const adults =
      typeof adultsRaw === "number"
        ? adultsRaw
        : typeof adultsRaw === "string" && adultsRaw.trim() !== ""
          ? Number(adultsRaw)
          : undefined;
    const childAges =
      typeof search.childAges === "string"
        ? search.childAges
        : typeof search.childAges === "number"
          ? String(search.childAges)
          : undefined;
    const city = typeof search.city === "string" && search.city.trim() !== "" ? search.city : undefined;
    const sort = typeof search.sort === "string" && search.sort.trim() !== "" ? search.sort : undefined;
    return {
      ...(checkin ? { checkin } : {}),
      ...(childAges ? { childAges } : {}),
      ...(checkout ? { checkout } : {}),
      ...(adults !== undefined && Number.isFinite(adults) ? { adults } : {}),
      ...(city ? { city } : {}),
      ...(sort ? { sort } : {}),
    };
  },
  beforeLoad: ({ params, search }) => {
    // The dates and party are carried across. A guest who priced a stay and
    // then followed an old bookmark should not have to enter them again, and
    // an unpriced list is the one thing this page must never hand back.
    throw redirect({
      to: "/$lang/category/$category",
      params: {
        lang: params.lang,
        category: isStayPropertyType(params.city) ? params.city : "stays",
      },
      search: search as never,
      statusCode: 301,
      replace: true,
    });
  },
});
